import { Button } from '@heidi/design-system/button';
import { Textarea } from '@heidi/design-system/textarea';
import { cn } from '@heidi/utils';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { MessageDescriptor } from 'react-intl';
import { useIntl } from 'react-intl';

import type { ApprovalVerdict, ToolUseItem } from '../../../../contracts';
import type { ApprovalInputErrorCode } from '../../../../data';
import { parseApprovalInput } from '../../../../data';
import { ApprovalHistory } from '../approval-history/approval-history';
import { toolUseRowMessages } from './tool-use-row.messages';

/**
 * Exhaustive by construction: a new ApprovalInputErrorCode fails to compile
 * here until it gets its own localized message.
 */
const approvalInputErrorMessages: Record<ApprovalInputErrorCode, MessageDescriptor> = {
  not_an_object: toolUseRowMessages.inputNotAnObject,
  invalid_json: toolUseRowMessages.inputInvalidJson,
};

export interface ToolUseRowProps {
  item: ToolUseItem;
  /**
   * Resolve a pending HITL approval. Returns a Promise that rejects
   * on BE failure so callers (an inline approval card) can clear
   * their pending-verdict latch. Fire-and-forget at this generic row
   * is fine — we discard the Promise with ``void`` at the call sites.
   */
  onResolveApproval?: (
    approvalId: string,
    verdict: ApprovalVerdict,
    reason?: string,
    modifiedInput?: Record<string, unknown> | null
  ) => Promise<void> | void;
  resolvingApproval?: boolean;
}

export const ToolUseRow = ({
  item,
  onResolveApproval,
  resolvingApproval = false,
}: ToolUseRowProps) => {
  const intl = useIntl();
  const [reason, setReason] = useState('');
  const originalInputJson = useMemo(
    () => JSON.stringify(item.input, null, 2),
    [item.input]
  );
  const [modifiedInputText, setModifiedInputText] = useState(originalInputJson);
  const approvalInput = parseApprovalInput(modifiedInputText, originalInputJson);
  const canResolve = !!item.pendingApprovalId && !!onResolveApproval;

  // Seed the editable input once per approval, keyed on the approval id —
  // not on the serialized input. Timeline items are re-flattened on every
  // refetch, so resetting on `originalInputJson` would discard in-progress
  // edits whenever the snapshot re-serialized the same tool input.
  const seededApprovalIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const approvalId = item.pendingApprovalId;
    if (approvalId && seededApprovalIdRef.current !== approvalId) {
      setModifiedInputText(originalInputJson);
      seededApprovalIdRef.current = approvalId;
    }
  }, [item.pendingApprovalId, originalInputJson]);

  return (
    <div className={cn('rounded-xl border p-3 text-sm', toneClassForStatus(item.status))}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="font-medium text-text-primary">{item.name}</div>
          <div className="text-xs text-text-tertiary">
            {intl.formatMessage(toolUseRowMessages.status, { status: item.status })}
          </div>
        </div>
        {item.isSubAgent && (
          <span className="rounded-full bg-surface-container px-2 py-1 text-xs text-text-secondary">
            {intl.formatMessage(toolUseRowMessages.subAgent)}
          </span>
        )}
      </div>

      <details className="mt-3">
        <summary className="cursor-pointer text-xs text-text-secondary">
          {intl.formatMessage(toolUseRowMessages.inputSummary)}
        </summary>
        <pre className="mt-2 overflow-auto rounded-md bg-surface p-2 text-xs">
          {originalInputJson}
        </pre>
      </details>

      {item.live_output && !item.result && (
        <pre className="mt-3 max-h-48 overflow-auto rounded-md bg-surface p-2 text-xs">
          {item.live_output}
        </pre>
      )}

      {item.result && (
        <div className="mt-3">
          <div className="mb-1 text-xs font-medium text-text-secondary">
            {item.result.is_error
              ? intl.formatMessage(toolUseRowMessages.errorHeading)
              : intl.formatMessage(toolUseRowMessages.outputHeading)}
          </div>
          <pre className="max-h-64 overflow-auto rounded-md bg-surface p-2 text-xs">
            {formatResult(item.result.content)}
          </pre>
        </div>
      )}

      {item.pendingApprovalId && (
        <div className="mt-3 rounded-lg border border-border bg-surface-container p-3">
          <div className="mb-2 text-xs font-medium text-text-primary">
            {intl.formatMessage(toolUseRowMessages.approvalRequired)}
          </div>
          <Textarea
            value={modifiedInputText}
            onChange={(event) => setModifiedInputText(event.target.value)}
            placeholder={intl.formatMessage(toolUseRowMessages.inputPlaceholder)}
            className="mb-2 min-h-28 w-full rounded-md border border-border bg-surface p-2 font-mono text-xs"
          />
          {approvalInput.error && (
            <div className="mb-2 text-xs text-validation-destructive">
              {intl.formatMessage(approvalInputErrorMessages[approvalInput.error])}
            </div>
          )}
          <Textarea
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder={intl.formatMessage(toolUseRowMessages.reasonPlaceholder)}
            className="mb-2 min-h-16 w-full rounded-md border border-border bg-surface p-2 text-sm"
          />
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={!canResolve || resolvingApproval}
              onClick={() => {
                if (item.pendingApprovalId) {
                  void onResolveApproval?.(item.pendingApprovalId, 'deny', reason, null);
                }
              }}
            >
              {intl.formatMessage(toolUseRowMessages.deny)}
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={!canResolve || resolvingApproval || !approvalInput.valid}
              onClick={() => {
                if (item.pendingApprovalId) {
                  void onResolveApproval?.(
                    item.pendingApprovalId,
                    'approve',
                    reason,
                    approvalInput.modifiedInput
                  );
                }
              }}
            >
              {intl.formatMessage(toolUseRowMessages.approve)}
            </Button>
          </div>
        </div>
      )}

      {item.approval && (
        <div className="mt-3">
          <ApprovalHistory approval={item.approval} />
        </div>
      )}
    </div>
  );
};

const toneClassForStatus = (status: ToolUseItem['status']) => {
  switch (status) {
    case 'running':
    case 'streaming_input':
      return 'border-validation-warning-border bg-validation-warning-muted/40';
    case 'error':
      return 'border-validation-destructive-border bg-validation-destructive-muted/40';
    case 'pending_approval':
      return 'border-validation-warning-border bg-validation-warning-muted/40';
    case 'cancelled':
    case 'complete':
    case 'queued':
      return 'border-border bg-surface-container';
  }
};

const formatResult = (content: unknown) =>
  typeof content === 'string' ? content : JSON.stringify(content, null, 2);
